import React, {memo, useContext} from "react";
import FlexBox from "view/components/Layout/FlexBox";
import {Button, IconButton, useTheme} from "@material-ui/core";
import TextareaAutosize from "react-textarea-autosize";
import AlertIcon from "view/images/AlertIcon";
import {FormContext} from "view/components/Form";
import SubjectIcon from "@material-ui/icons/Subject";
import {Controller, UseFormReturn} from "react-hook-form";
import {TextResponseCell} from "ts/api/task";
import {CellType} from "view/components/GridResponseBlock/types";
import clsx from "clsx";
import {useTranslate} from "@tolgee/react";
import useStyles from "./styles";

type GridResponseFormShape = {
  grid?: {cells: {type: string; text?: string}[]}[];
};

type TextInputProps = {
  isRequired: boolean;
  viewOnly?: boolean;
  columnIndex: number;
  rowIndex: number;
  isJustSavedDraft?: React.MutableRefObject<boolean>;
  openParagraph?: (rowIndex: number, columnIndex: number) => void;
};

function TextResponseInput({
  isRequired,
  viewOnly,
  openParagraph,
  rowIndex,
  isJustSavedDraft,
  columnIndex,
}: TextInputProps) {
  const classes = useStyles();
  const theme = useTheme();
  const {t} = useTranslate();

  const form = useContext(FormContext) as UseFormReturn<GridResponseFormShape>;

  const {control, formState, watch} = form;

  const {text} = (watch(`grid.${rowIndex}.cells.${columnIndex}`) ||
    {}) as TextResponseCell;

  const isError =
    formState?.errors?.grid?.[rowIndex]?.cells?.[columnIndex]?.type ===
    "required";

  return (
    <>
      <FlexBox
        justifyContent="space-between"
        alignCenter
        height="100%"
        width="100%"
        position="relative"
      >
        <Controller
          name={`grid.${rowIndex}.cells.${columnIndex}`}
          defaultValue={{type: CellType.Text, text: ""}}
          control={control}
          disabled={viewOnly}
          rules={{
            validate: {
              required: (value) =>
                isRequired && value.type === "text"
                  ? !!value.text?.trim()?.length
                  : true,
            },
          }}
          render={({field}) => {
            const {text: fieldText} = (field.value || {}) as TextResponseCell;
            return (
              <TextareaAutosize
                placeholder={t("common.tasks.grid.cell.text_placeholder")}
                minRows={1}
                maxRows={10}
                disabled={viewOnly}
                onChange={({target}) => {
                  const newVal = {text: target.value, type: CellType.Text};
                  if (isJustSavedDraft?.current) {
                    isJustSavedDraft.current = false;
                  }
                  return field.onChange(newVal);
                }}
                value={text ?? fieldText ?? ""}
                className={clsx(
                  classes.textResponseInput,
                  isError && classes.errorField
                )}
              />
            );
          }}
        />
        {isRequired && (
          <Button className={classes.makeColumnMandatoryButton} disabled>
            <AlertIcon fill={isError ? theme.palette.error.main : undefined} />
          </Button>
        )}
      </FlexBox>
      {openParagraph && (
        <FlexBox alignCenter justifyContent="center">
          <IconButton
            className={`${classes.cellButton} ${classes.subjectButton}`}
            onClick={() => openParagraph(rowIndex, columnIndex)}
          >
            <SubjectIcon className={classes.subjectIcon} />
          </IconButton>
        </FlexBox>
      )}
    </>
  );
}

export default memo(TextResponseInput);
